import { useCallback } from "react";

import useSWR from "swr";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { buildSwrConfig } from "./utils";

import type { AxiosError } from "axios";
import type { KeyedMutator, SWRConfiguration } from "swr";

interface UseUnauthenticatedSwrFetchProps {
  url: string | null;
  errorMessage: string;
  errorMessageKey?: string;
  swrOptions?: SWRConfiguration;
  onError?: (error: AxiosError) => void;
}

interface UseUnauthenticatedSwrFetchResult<T> {
  data: T | undefined;
  loading: boolean;
  validating: boolean;
  error: string | null;
  refetch: () => void;
  mutate: KeyedMutator<T>;
}

export function useUnauthenticatedSwrFetch<T>(
  props: UseUnauthenticatedSwrFetchProps,
): UseUnauthenticatedSwrFetchResult<T> {
  const { url, errorMessage, errorMessageKey, swrOptions, onError } = props;
  const { unauthenticatedApi } = useAPI();

  const {
    data,
    error: swrError,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<T, AxiosError>(
    url,
    async (fetchUrl: string) =>
      unauthenticatedApi<T>("get", fetchUrl).then((response) => response.data),
    buildSwrConfig<T, AxiosError>(
      { revalidateOnFocus: false, revalidateOnReconnect: false },
      swrOptions,
      onError,
    ),
  );

  const error = swrError
    ? extractAPIRequestErrorMessage(swrError, errorMessage, errorMessageKey)
    : null;

  const refetch = useCallback(() => {
    mutate();
  }, [mutate]);

  return {
    data,
    loading: isLoading,
    validating: isValidating,
    error,
    refetch,
    mutate,
  };
}
